import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash, FileText, Certificate, Warning } from "@phosphor-icons/react";
import { Pill, QUAL_TONE, QUAL_LABEL, fmtD } from "./hsUtils";
import { openAuthFile } from "../../lib/fileOpen";

export function CompetencyMatrix({ data, settings, canEdit, onChanged }) {
  const [dlg, setDlg] = useState(null);
  const [expanded, setExpanded] = useState("");
  const types = data.types || [];
  const required = new Set((data.required || []).map((r) => r.key));
  const columns = types.filter((t) => required.has(t.key) || data.operatives.some((o) => o.by_type[t.key]));
  const remove = async (q) => {
    if (!window.confirm(`Remove ${q.type_name} for ${q.user_name}?`)) return;
    try { await API.delete(`/hs/qualifications/${q.id}`); toast.success("Removed"); onChanged(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  if (!data.operatives.length) return <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="hs-competency-empty">No in-house operatives yet — add your team under In-House Team → Team.</div>;
  return (
    <div className="space-y-3" data-testid="hs-competency">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-[11px] text-muted-foreground">
            <tr><th className="text-left p-2 font-medium sticky left-0 bg-muted/40">Operative</th>
              {columns.map((t) => <th key={t.key} className="p-2 font-medium text-left whitespace-nowrap">{t.name}{required.has(t.key) && <span className="text-primary" title="Required for this trade"> *</span>}</th>)}
              <th className="p-2" /></tr>
          </thead>
          <tbody>
            {data.operatives.map((o) => (
              <tr key={o.id} className="border-t border-border align-top" data-testid={`hs-op-${o.id}`}>
                <td className="p-2 sticky left-0 bg-card">
                  <button className="text-left" onClick={() => setExpanded(expanded === o.id ? "" : o.id)} data-testid={`hs-op-toggle-${o.id}`}>
                    <p className="font-semibold text-sm">{o.name}</p>
                    <p className="text-[11px] text-muted-foreground">{o.trade || "No trade set"} · {o.qualifications.length} qual{o.qualifications.length === 1 ? "" : "s"}</p>
                    {o.warnings.length > 0 && <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-0.5"><Warning size={11} /> {o.warnings.length} warning{o.warnings.length === 1 ? "" : "s"}</p>}
                  </button>
                  {expanded === o.id && (
                    <div className="mt-2 space-y-1" data-testid={`hs-op-detail-${o.id}`}>
                      {o.qualifications.map((q) => (
                        <div key={q.id} className="flex items-center gap-2 rounded-md border border-border p-1.5">
                          <Pill tone={QUAL_TONE[q.status]}>{QUAL_LABEL[q.status]}</Pill>
                          <span className="flex-1 min-w-0"><span className="font-medium">{q.type_name}</span>{q.reference && <span className="text-muted-foreground"> · {q.reference}</span>}{q.expires_at && <span className="text-muted-foreground"> · exp {fmtD(q.expires_at)}</span>}</span>
                          {q.certificate_file_id && <button onClick={() => openAuthFile(`/hs/qualifications/${q.id}/certificate`)} title="View certificate" className="text-primary" data-testid={`hs-qual-cert-${q.id}`}><Certificate size={14} /></button>}
                          {canEdit && <button onClick={() => remove(q)} className="text-muted-foreground hover:text-red-600" data-testid={`hs-qual-remove-${q.id}`}><Trash size={13} /></button>}
                        </div>
                      ))}
                      {!o.qualifications.length && <p className="text-[11px] text-muted-foreground italic">No qualifications recorded.</p>}
                    </div>
                  )}
                </td>
                {columns.map((t) => { const q = o.by_type[t.key]; return (
                  <td key={t.key} className="p-2">
                    {q ? <Pill tone={QUAL_TONE[q.status]} testid={`hs-cell-${o.id}-${t.key}`}>{QUAL_LABEL[q.status]}{q.expires_at ? ` · ${fmtD(q.expires_at)}` : ""}</Pill>
                      : required.has(t.key) ? <Pill tone={QUAL_TONE.missing} testid={`hs-cell-${o.id}-${t.key}`}>Missing</Pill> : <span className="text-muted-foreground">—</span>}
                  </td>); })}
                <td className="p-2 text-right">{canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDlg(o)} data-testid={`hs-add-qual-${o.id}`}><Plus size={12} className="mr-1" /> Add</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">* required for the selected trade. Warnings appear when a qualification is missing, expired or expires within {settings?.qual_warn_days || 60} days — assignment is never blocked.</p>
      <QualificationDialog operative={dlg} types={types} onClose={() => setDlg(null)} onSaved={onChanged} />
    </div>
  );
}

export function QualificationDialog({ operative, types, onClose, onSaved }) {
  const [form, setForm] = useState({ type_key: "", reference: "", issued_at: "", expires_at: "", notes: "", file: null });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.type_key) { toast.error("Pick a qualification type"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("user_id", operative.id);
      ["type_key", "reference", "issued_at", "expires_at", "notes"].forEach((k) => fd.append(k, form[k] || ""));
      if (form.file) fd.append("certificate", form.file);
      await API.post("/hs/qualifications", fd);
      toast.success("Qualification added"); onSaved(); onClose(); setForm({ type_key: "", reference: "", issued_at: "", expires_at: "", notes: "", file: null });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!operative} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="hs-qual-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Add qualification — {operative?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Qualification</Label>
            <Select value={form.type_key} onValueChange={(v) => setForm({ ...form, type_key: v })}><SelectTrigger data-testid="hs-qual-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="max-h-72">{types.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Certificate / card no.</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="hs-qual-ref" /></div>
            <div><Label>Issued</Label><Input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} data-testid="hs-qual-issued" /></div>
          </div>
          <div><Label>Expires (leave blank if it doesn't)</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="hs-qual-expires" /></div>
          <div><Label>Certificate file (optional)</Label><input type="file" accept="image/*,application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="text-sm block mt-1" data-testid="hs-qual-file" /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="hs-qual-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="hs-qual-save"><FileText size={14} className="mr-1" /> {saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
